/**
 * Thin fetch wrapper: base path, the dev-mode actor header, typed errors.
 *
 * There is no token provider and no Authorization header. ADR-005 / SALVAGE
 * §4 D1: the credential is an httpOnly session cookie the browser attaches to
 * every same-origin request by itself, so this module has nothing to hold and
 * nothing to leak.
 */
import { envOr } from '@/lib/env';

const BASE = envOr(import.meta.env.VITE_API_BASE, '/api');

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
    /** The API's stable machine-readable code (`forbidden`, `second_approver_required`, …) when it sent one. */
    readonly code: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isAuth() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }

  get isMissing() {
    return this.status === 404;
  }
}

/**
 * Retained for the shell's retry predicate, and no longer thrown.
 *
 * It existed because msal could leave the app running with no token: requests
 * went out bare, came back 401, and TanStack Query cached the 401 and never
 * retried — which flipped the shell to the sign-in screen and looped. With a
 * session cookie there is no such window, so nothing raises this any more.
 */
export class AuthNotReadyError extends Error {
  constructor(readonly path: string) {
    super('Not signed in yet — the request was not sent.');
    this.name = 'AuthNotReadyError';
  }
}

/** AUTH_MODE=dev: the seeded user the API should act as. Ignored server-side in every other mode. */
let devActor: string | null = null;
/** How this build authenticates, learned at runtime from /api/auth/config-check. */
let authMode: 'entra' | 'google' | 'dev' = 'entra';

export function setAuthMode(mode: 'entra' | 'google' | 'dev') {
  authMode = mode;
}

export function getAuthMode() {
  return authMode;
}

export function setDevActor(userKey: string | null) {
  devActor = userKey;
}

export function getDevActor() {
  return devActor;
}

/** #705 (T-060): an Admin's "Show QA records" — every read then asks for QA records too (the server ignores it for anyone else). */
let showQa = false;
export function setShowQa(on: boolean) {
  showQa = on;
}
export function getShowQa() {
  return showQa;
}
const READS: ReadonlySet<string> = new Set(['GET']);
export function withShowQa(path: string, method: string): string {
  if (!showQa || !READS.has(method)) return path;
  return `${path}${path.includes('?') ? '&' : '?'}showQa=1`;
}

/**
 * Auth headers for a request this module does not make itself — multipart uploads, EventSource
 * polyfills, anything that cannot go through apiFetch. Under a real provider this is EMPTY: the
 * session cookie rides along on its own. In AUTH_MODE=dev it names the chosen seeded user, which
 * is how a test client with no cookie jar authenticates.
 */
export async function getAuthHeaders(): Promise<Record<string, string>> {
  return authMode === 'dev' && devActor ? { 'x-cigs-dev-user': devActor } : {};
}

async function credentialedHeaders(_path: string): Promise<Record<string, string>> {
  return getAuthHeaders();
}

/**
 * Turn a failed response body into the ApiError the user sees. The API's one error shape is
 * `{ error: { code, message } }` (api/src/lib/errors.ts) and its message is written to be shown —
 * a 403 names the role that is missing — so it wins; a bare top-level `message` is next; the
 * status line is the last resort. This is the ONLY place that text is derived.
 */
export function errorFromBody(status: number, statusText: string, text: string): ApiError {
  let parsed: unknown = text;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    /* keep the raw text */
  }
  const obj = parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  const nested = obj?.error && typeof obj.error === 'object' ? (obj.error as Record<string, unknown>) : null;
  const pick = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : null);
  const message = pick(nested?.message) ?? pick(obj?.message) ?? `${status} ${statusText}`.trim();
  const code = pick(nested?.code) ?? pick(obj?.code);
  return new ApiError(status, message, parsed, code);
}

/** POST a FormData body (document upload) with auth attached and errors shaped like apiFetch. */
export async function apiUpload<T>(path: string, form: FormData, opts: { correlationId?: string } = {}): Promise<T> {
  const headers = await credentialedHeaders(path);
  if (opts.correlationId) headers['x-correlation-id'] = opts.correlationId;
  const res = await fetch(`${BASE}${path}`, { method: 'POST', headers, body: form, credentials: 'same-origin' });
  if (!res.ok) throw errorFromBody(res.status, res.statusText, await res.text());
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

export interface RequestOptions {
  method?: string;
  body?: unknown;
  signal?: AbortSignal;
  /** correlation id groups the writes of one user action in change_log */
  correlationId?: string;
}

export async function apiFetch<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...(await credentialedHeaders(path)),
  };
  if (opts.body !== undefined) headers['Content-Type'] = 'application/json';
  if (opts.correlationId) headers['x-correlation-id'] = opts.correlationId;

  const res = await fetch(`${BASE}${withShowQa(path, opts.method ?? 'GET')}`, {
    method: opts.method ?? 'GET',
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal,
    // The session cookie is the credential (ADR-005). Same-origin is the
    // browser default, said out loud so a future base-URL change is a visible
    // decision rather than a silent sign-out.
    credentials: 'same-origin',
  });

  if (!res.ok) throw errorFromBody(res.status, res.statusText, await res.text());

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Downloads a file produced by the API (CSV / XLSX exports) with auth headers attached.
 *
 * #163: a failure here used to throw a generic "Export failed (401)" instead of the API's own
 * refusal text ("You are not signed in, or your session has ended…"), which is the one place
 * `errorFromBody` is meant to be the source of. A 401 also notifies `onAuthExpired` listeners so
 * the shell — which never sees this fetch, since it is outside TanStack Query — finds out the
 * session ended instead of leaving the user to guess from a failed download.
 */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    headers: await credentialedHeaders(path),
    credentials: 'same-origin',
  });
  if (!res.ok) {
    const error = errorFromBody(res.status, res.statusText, await res.text());
    if (error.isAuth) notifyAuthExpired();
    throw error;
  }
  const blob = await res.blob();
  triggerDownload(blob, filename);
}

export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function newCorrelationId(): string {
  return crypto.randomUUID();
}

/**
 * #163: `apiDownload` runs outside TanStack Query, so a 401 there (the session expired mid-visit)
 * never touches the `me` query cache the shell watches. This is the narrow hook-up: `apiDownload`
 * calls every registered listener on a 401; `AppProviders` (the one place with a `QueryClient`)
 * registers one that invalidates the `me` query so the shell notices and re-probes the session.
 */
const authExpiredListeners = new Set<() => void>();

export function onAuthExpired(listener: () => void): () => void {
  authExpiredListeners.add(listener);
  return () => authExpiredListeners.delete(listener);
}

function notifyAuthExpired(): void {
  for (const listener of authExpiredListeners) listener();
}
